<template>
  <AppTable>
    <caption>
      Översikt
    </caption>
    <thead>
      <tr>
        <th scope="col">Lånenummer</th>
        <th scope="col">Typ av lån</th>
        <th scope="col" class="num">Kapitalskuld</th>
        <th scope="col" class="num">Ränta</th>
        <th scope="col">Räntebindning</th>
        <th scope="col">Ränteändringsdag</th>
      </tr>
    </thead>
    <tbody>
      <AppTableRow v-for="loan in loans" :key="loan.id">
        <AppTableCell nowrap>
          <a class="loan-link" :href="`#${loanAnchorId(loan.publicLoanNumber)}`">{{ loan.publicLoanNumber }}</a>
        </AppTableCell>
        <AppTableCell nowrap>{{ t(`LOAN_TYPE.${loan.type}`) }}</AppTableCell>
        <AppTableCell nowrap class="num num--strong">{{
          formatAmountWithCurrency(loan.principal.current)
        }}</AppTableCell>
        <AppTableCell nowrap class="num">{{ formatPercentage(loan.pricing.interestRate) }}</AppTableCell>
        <AppTableCell nowrap>{{ t(`INTEREST_TERM.${loan.interestTerm.interestTerm}`) }}</AppTableCell>
        <AppTableCell nowrap>
          {{ formatDateIfValid(loan.interestTerm.nextInterestTermsChangeDate) ?? '-' }}
          <span v-if="daysUntilLabel(loan)" class="text-subtle">({{ daysUntilLabel(loan) }})</span>
        </AppTableCell>
      </AppTableRow>

      <AppTableRow class="total-row">
        <AppTableCell colspan="2" nowrap>Totalt ({{ loans.length }} lån)</AppTableCell>
        <AppTableCell nowrap class="num">{{ formatAmountWithCurrency(totalPrincipal) }}</AppTableCell>
        <AppTableCell colspan="3" />
      </AppTableRow>
    </tbody>
  </AppTable>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { AppTable, AppTableCell, AppTableRow } from '@staff/ui'
import { formatAmountWithCurrency, formatDateIfValid, formatPercentage } from '@staff/shared'

import type { Loan } from '../debt.types'
import { loanAnchorId } from '../debt.utils'

const { t } = useI18n()

defineProps<{
  loans: Loan[]
  totalPrincipal: number
}>()

// "om X dagar" when the rate change date is near — a key thing this table is scanned for.
const daysUntilLabel = (loan: Loan) => {
  const dateStr = loan.interestTerm.nextInterestTermsChangeDate
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return null
  const diffDays = Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  if (diffDays < 0 || diffDays > 90) return null
  if (diffDays === 0) return 'idag'
  return diffDays === 1 ? 'om 1 dag' : `om ${diffDays} dagar`
}
</script>

<style scoped lang="scss">
.num--strong {
  font-weight: var(--font-weight-medium);
}

// No SectionCard around this table — the caption is the section heading, unfilled on purpose.
caption {
  padding-bottom: 12px;
  text-align: left;
  font-size: 18px;
  font-weight: 600;
}

// Same look as AppTableCell's own links (that rule is scoped and misses slotted links).
.loan-link {
  font-weight: 500;
  color: var(--color-accent-fg);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

th.num,
:deep(td.num) {
  text-align: right;
}

// Set apart from the loan rows by the rule above it and its weight.
.total-row :deep(> td) {
  border-top: 2px solid var(--color-border-hover);
  font-weight: 600;
}
</style>
